import { useContext, useEffect } from "react"
import queryString from "query-string"
import { useStaticQuery, graphql } from "gatsby"
import { stockCheck } from "../utils/stock-check"
import { StoreContext } from "../context/store-context"

const ProductsQuery = graphql`
  query productsQuery {
    prices: allStripePrice(filter: { active: { eq: true }, product: { active: { eq: true }, metadata: { Code: {eq: null}}} })
    {
      edges {
        node {
          id
          created
          active
          currency
          unit_amount
          metadata {
            Stock
            Preview
          }
          product {
            id
            name
            images
            metadata {
              Type
            }
          }
        }
      }
    }
    productSlugs: allStripeProduct(filter: {active: {eq: true}})
    {
      edges {
        node {
          id
          slug: gatsbyPath(filePath:"/products/{stripeProduct.metadata__Type}/{stripeProduct.name}")
        }
      }
    }
    productImages: allContentfulAsset
    {
      edges {
        node {
            id
            title
            description
            lg: gatsbyImageData(aspectRatio: 1, width: 640)
        }
      }
    }
    generalImages: allContentfulAsset(filter: {description: {eq: "General"}})
    {
      edges {
        node {
            id
            title
            description
            lg: gatsbyImageData(aspectRatio: 1, width: 640)
        }
      }
    }
    content: allContentfulProduct {
      nodes {
        productCode
        productId
        tags
      }
    }
  }
`
function makeQueryStringValue(allItems, selectedItems) {
  if (allItems.length === selectedItems.length) {
    return []
  }
  return selectedItems
}

function arrayify(value) {
  if (!value) {
    return []
  }
  if (!Array.isArray(value)) {
    return [value]
  }
  return value
}

/**
 * Extracts default search values from the query string
 * @param {string} query
 */
export function getValuesFromQueryString(query) {
  const {
    q: term,
    s: sortKey,
    x: maxPrice,
    n: minPrice,
    p,
    t
  } = queryString.parse(query)
  return {
    term,
    sortKey,
    maxPrice,
    minPrice,
    productTypes: arrayify(p),
    tags: arrayify(t)
  }
}

export function useProductSearch(
  filters,
  { allProductTypes, allTags },
  sortKey
) {

  const { term, productTypes, tags, minPrice, maxPrice } = filters

  // Relevance is non-deterministic if there is no query, so we default to "title" instead
  const initialSortKey = filters.term ? "RELEVANCE" : "TITLE"

  const result = useStaticQuery(ProductsQuery)

  useEffect(() => {
    const qs = queryString.stringify({
      // Don't show if falsy
      q: term || undefined,
      x: maxPrice || undefined,
      n: minPrice || undefined,
      // Don't show if sort order is default
      s: sortKey === initialSortKey ? undefined : sortKey,
      // Don't show if all values are selected
      p: makeQueryStringValue(allProductTypes, productTypes),
      t: makeQueryStringValue(allTags, tags)
    })

    const url = new URL(window.location.href)
    url.search = qs
    url.hash = ""
    window.history.replaceState({}, null, url.toString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result])

  const filterCount =
    (filters.tags.length === allTags.length ? 0 : filters.tags.length) +
    (filters.productTypes.length === allProductTypes.length
      ? 0
      : filters.productTypes.length) +
    (filters.minPrice ? 1 : 0) +
    (filters.maxPrice ? 1 : 0)

  // Group prices by product
  const allProducts = {}
  for (const { node: price } of result.prices.edges) {
    const product = price.product
    if (!allProducts[product.id]) {
      allProducts[product.id] = product
      allProducts[product.id].isMatch = true
      allProducts[product.id].prices = []
      const productSlug = result.productSlugs.edges.find(x => x.node.id === product.id);
      allProducts[product.id].slug = productSlug.node.slug;
      allProducts[product.id].typeSlug = productSlug.node.typeSlug;
      allProducts[product.id].images = result.productImages.edges
        .filter(x => x.node.description === product.id)
        .map(x => x.node)
        .sort((a, b) => a.title.localeCompare(b.title))
      allProducts[product.id].images = allProducts[product.id].images.concat((result.generalImages.edges
        .map(x => x.node)
        .filter(x => x.title.startsWith(product.metadata.Type))
        .sort((a, b) => a.title.localeCompare(b.title))))

      allProducts[product.id].content = result.content.nodes.find(x => x.productCode === product.id);

    }
    allProducts[product.id].prices.push(price)
  }

  if (filters.term) {
    let termWordsLower = filters.term.toLowerCase().trim().split(' ').filter(x => x !== '');
    Object.keys(allProducts).forEach(x => {
      allProducts[x].isMatch &= termWordsLower.every(y =>
        allProducts[x].name.toLowerCase().indexOf(y) > -1 ||
        allProducts[x].content?.productId?.toLowerCase().indexOf(y) > -1 ||
        allProducts[x].metadata.Type.toLowerCase().indexOf(y) > -1 ||
        (allProducts[x].content &&
          allProducts[x].content.tags &&
          allProducts[x].content.tags.some(t => t.toLowerCase().indexOf(y) > -1)));
    });
  }

  if (filters.productTypes.length > 0) {
    Object.keys(allProducts).forEach(x => { allProducts[x].isMatch &= filters.productTypes.indexOf(allProducts[x].metadata.Type) > -1 });
  }

  if (filters.tags.length > 0) {
    Object.keys(allProducts).forEach(x => { allProducts[x].isMatch &= allProducts[x].content.tags && allProducts[x].content.tags.some(t => filters.tags.includes(t)) });
  }

  if (filters.minPrice) {
    Object.keys(allProducts).forEach(x => { allProducts[x].isMatch &= allProducts[x].prices[0].unit_amount >= filters.minPrice * 100 });
  }

  if (filters.maxPrice) {
    Object.keys(allProducts).forEach(x => { allProducts[x].isMatch &= allProducts[x].prices[0].unit_amount <= filters.maxPrice * 100 });
  }

  // now sort
  // const activeSortKey = sortKey ? sortKey : initialSortKey
  // switch (activeSortKey) {
  //   case "RELEVANCE":
  //     break;
  //   case "PRICE":
  //     filteredProducts = filteredProducts.sort((a, b) => a.prices[0].unit_amount - b.prices[0].unit_amount)
  //     break;
  //   case "TITLE":
  //     filteredProducts = filteredProducts.sort((a, b) => a.name.localeCompare(b.name))
  //     break;
  //   case "CREATED_AT":
  //     filteredProducts = filteredProducts.sort((a, b) => b.created - a.created)
  //     break;
  //   default:
  //     break;
  // }

  // filteredProducts = filteredProducts.reduce((a, x) => ({ ...a, [x.id]: x }), {})

  const qs = queryString.stringify({
    // Don't show if falsy
    q: term || undefined,
    x: maxPrice || undefined,
    n: minPrice || undefined,
    // Don't show if sort order is default
    s: sortKey === initialSortKey ? undefined : sortKey,
    // Don't show if all values are selected
    p: makeQueryStringValue(allProductTypes, productTypes),
    t: makeQueryStringValue(allTags, tags)
  })

  return {
    data: allProducts,
    filterCount,
    qs
  }
}

export function useStockCheck(products) {
  const { stockLevels, updateStockLevels, initialised } = useContext(StoreContext)
  const debounceSecs = 10;

  useEffect(() => {
    if (!initialised) {
      return;
    }
    async function doStockCheck() {
      // Only fetch stock for prices that, at build time, either had a stock set (including 0) or were in preview. Also apply debounce since last lookup.
      // OR we have stored stock with preview, fetch these after debounce. Resolves issue where site is now built without preview, but still preview in storage.
      let filteredPriceIds = products.flatMap(product =>
        product.prices.filter(price =>
        (
          ((price.metadata.Preview == 1 || price.metadata.Stock !== null) &&
            !stockLevels.find(stockLevel => stockLevel.priceId == price.id && (Date.now() - stockLevel.ts) < debounceSecs * 1000))
          || (stockLevels.find(stockLevel => stockLevel.priceId == price.id && stockLevel.pr && (Date.now() - stockLevel.ts) >= debounceSecs * 1000))
        )
        ))
        .map(price => price.id)
      updateStockLevels(await stockCheck(filteredPriceIds))
    }
    doStockCheck()
  }, [initialised])
}
